import { motion } from "framer-motion";
import { Check, Minus, Zap } from "lucide-react";
import { useT } from "@/i18n";
import { fmtPrice } from "@/lib/content";

function Cell({ value, popular }) {
  const v = (value || "").trim();
  if (!v || v === "no") return <Minus size={16} className="mx-auto text-slate-300 dark:text-slate-600" aria-label="—" />;
  if (v === "yes") return <Check size={18} className={`mx-auto ${popular ? "text-brand-yellow" : "text-brand-teal"}`} aria-label="✓" />;
  if (v.startsWith("!")) {
    return (
      <span className={`inline-flex items-center gap-1.5 font-bold rounded-full px-3 py-1 text-xs ${popular ? "bg-brand-yellow text-brand-navy" : "bg-brand-teal/10 text-brand-tealDark dark:text-brand-teal"}`}>
        <Zap size={12} className="fill-current" /> {v.slice(1).trim()}
      </span>
    );
  }
  return <span className="text-xs sm:text-sm leading-snug">{v}</span>;
}

export default function PackageCompare({ packages, rows, cfg, onOrder }) {
  const { t, lang } = useT();
  if (!rows?.length || !packages?.length) return null;
  const trRows = lang !== "pl" && t.pricing.compare_rows?.length === rows.length ? t.pricing.compare_rows : null;
  const cols = packages.map((p) => {
    const tr = (t.pricing.pkgs && t.pricing.pkgs[p.id]) || {};
    return { ...p, label: lang === "pl" ? p.name : tr.name || p.name, popular: p.id === "allinclusive" };
  });
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7 }}
      data-testid="package-compare"
      className="mt-16"
    >
      <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight">{t.pricing.compare_title}</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-2xl">{t.pricing.compare_sub}</p>
      <div className="mt-6 -mx-4 sm:mx-0 overflow-x-auto pb-2" data-lenis-prevent>
        <table className="w-full min-w-[640px] border-separate border-spacing-0 text-left">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-950 px-4 py-4 text-xs font-mono uppercase tracking-[0.2em] text-slate-500 rounded-tl-2xl border-b border-slate-200 dark:border-slate-800 w-[34%]">{t.pricing.compare_feature}</th>
              {cols.map((c) => (
                <th key={c.id} data-testid={`compare-head-${c.id}`} className={`px-4 py-4 text-center border-b border-slate-200 dark:border-slate-800 align-bottom ${c.popular ? "bg-brand-navy text-white rounded-t-2xl" : "bg-white dark:bg-slate-900"}`}>
                  <div className="font-display font-bold text-lg">{c.label}</div>
                  {c.tagline && <div className={`text-xs font-bold ${c.popular ? "text-brand-yellow" : "text-brand-teal"}`}>{lang === "pl" ? c.tagline : (t.pricing.pkgs?.[c.id]?.tagline || c.tagline)}</div>}
                  <div className={`mt-1 text-sm ${c.popular ? "text-slate-300" : "text-slate-500"}`}>
                    <span className={`font-display font-extrabold text-xl ${c.popular ? "text-white" : "text-slate-900 dark:text-white"}`}>{fmtPrice(c[cfg.field] || "—")}</span> {t.pricing.per_month}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid={`compare-row-${i}`} className="group">
                <td className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-950 px-4 py-3.5 text-sm font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200/70 dark:border-slate-800">{trRows ? trRows[i].label : r.label}</td>
                {cols.map((c) => (
                  <td key={c.id} className={`px-4 py-3.5 text-center border-b border-slate-200/70 dark:border-slate-800 ${c.popular ? "bg-brand-navy/[0.04] dark:bg-brand-teal/[0.06] text-slate-800 dark:text-slate-100" : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300"}`}>
                    <Cell value={trRows ? trRows[i].values?.[c.id] : r.values?.[c.id]} popular={false} />
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-950 px-4 py-4 rounded-bl-2xl" />
              {cols.map((c) => (
                <td key={c.id} className={`px-4 py-4 text-center ${c.popular ? "bg-brand-navy/[0.04] dark:bg-brand-teal/[0.06] rounded-b-2xl" : "bg-white dark:bg-slate-900"}`}>
                  <button
                    data-testid={`compare-select-${c.id}`}
                    onClick={() => onOrder(c.id)}
                    className={`w-full py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-colors duration-200 ${c.popular ? "bg-brand-navy text-white hover:bg-brand-teal" : "border border-brand-teal text-brand-tealDark dark:text-brand-teal hover:bg-brand-teal hover:text-white"}`}
                  >
                    {t.order.cta} {c.label}
                  </button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}
